<template>
  <v-card outlined class="mb-4">
    <v-card-text>
      <v-alert type="info" outlined dense class="mb-4">
        <v-icon left>mdi-information</v-icon>
        Odaberite proizvod koji ima definiran normativ. Sistem će automatski učitati potrebne sastojke.
      </v-alert>

      <v-row>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="product"
            :items="productsWithNormatives"
            item-text="name"
            item-value="id"
            return-object
            label="Proizvod s normativom"
            prepend-icon="mdi-food"
            outlined
            dense
            clearable
            hint="Proizvod već ima definiran normativ"
            persistent-hint
            @change="onProductChange"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  Šifra: {{ item.code }} | Normativ: {{ item.normative.length }} sastojaka
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
            <template v-slot:selection="{ item }">
              {{ item.name }} ({{ item.normative.length }} sastojaka)
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" md="6">
          <v-select
            v-model="warehouse"
            :items="warehouses"
            item-text="name"
            item-value="id"
            return-object
            label="Odredišno skladište"
            prepend-icon="mdi-warehouse"
            outlined
            dense
            hint="Skladište na koje ide gotov proizvod"
            persistent-hint
            :disabled="!product"
            @change="onWarehouseChange"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.location }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-select>
        </v-col>
      </v-row>

      <v-divider class="my-4"></v-divider>

      <v-row v-if="product">
        <v-col cols="12">
          <h3 class="text-h6 mb-3">Normativ proizvoda</h3>

          <v-card outlined color="blue lighten-5" class="mb-4">
            <v-card-text>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Sastojak</th>
                      <th class="text-right">Količina po 1 {{ product.unit }}</th>
                      <th class="text-right">Udio (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in product.normative" :key="index">
                      <td>{{ item.name }}</td>
                      <td class="text-right">
                        {{ item.quantity.toFixed(3) }} {{ item.unit }}
                      </td>
                      <td class="text-right">
                        {{ calculatePercentage(item).toFixed(2) }}%
                      </td>
                    </tr>
                    <tr class="grey lighten-3">
                      <td><strong>UKUPNO</strong></td>
                      <td class="text-right">
                        <strong>{{ totalNormativeWeight.toFixed(3) }} kg</strong>
                      </td>
                      <td class="text-right"><strong>100%</strong></td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="quantity"
                type="number"
                :label="`Željena količina (${product.unit})`"
                outlined
                dense
                :suffix="product.unit"
                :rules="[rules.required, rules.positive]"
                hint="Koliko proizvoda želite napraviti"
                persistent-hint
                prepend-icon="mdi-scale"
                @input="onQuantityChange"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-card outlined color="success lighten-5">
                <v-card-text class="text-center">
                  <div class="caption grey--text">Ukupna težina potrebne sirovine</div>
                  <div class="text-h5 success--text">
                    {{ (quantity * totalNormativeWeight).toFixed(2) }} kg
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-card outlined class="mt-3">
            <v-card-text>
              <h4 class="mb-2">Potrebne količine sastojaka:</h4>
              <v-chip
                v-for="(item, index) in product.normative"
                :key="index"
                class="ma-1"
                color="primary"
                outlined
              >
                {{ item.name }}: <strong class="ml-1">{{ (item.quantity * quantity).toFixed(2) }} {{ item.unit }}</strong>
              </v-chip>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'ProductSelector',
  props: {
    value: {
      type: Object,
      default: () => null
    }
  },
  data () {
    return {
      product: null,
      warehouse: null,
      quantity: 1,

      // Mock warehouses
      warehouses: [
        { id: 1, name: 'Glavno skladište', location: 'Zagreb' },
        { id: 2, name: 'Skladište sirovine', location: 'Zagreb' },
        { id: 3, name: 'Skladište gotovih proizvoda', location: 'Split' },
        { id: 4, name: 'Hladnjača', location: 'Rijeka' }
      ],

      // Mock products with normatives
      productsWithNormatives: [
        {
          id: 101,
          name: 'Ćevapčići',
          code: 'CEV-001',
          unit: 'kg',
          normative: [
            { itemId: 1, name: 'Junetina sirovina', quantity: 0.5, unit: 'kg' },
            { itemId: 3, name: 'Svinjetina sirovina', quantity: 0.5, unit: 'kg' },
            { itemId: 4, name: 'Mirodije mix', quantity: 0.1, unit: 'kg' }
          ]
        },
        {
          id: 102,
          name: 'Pljeskavice',
          code: 'PLJ-001',
          unit: 'kg',
          normative: [
            { itemId: 1, name: 'Junetina sirovina', quantity: 0.7, unit: 'kg' },
            { itemId: 3, name: 'Svinjetina sirovina', quantity: 0.3, unit: 'kg' },
            { itemId: 5, name: 'Sol', quantity: 0.02, unit: 'kg' },
            { itemId: 6, name: 'Papar crni', quantity: 0.01, unit: 'kg' }
          ]
        },
        {
          id: 103,
          name: 'Hamburger',
          code: 'HAM-001',
          unit: 'kg',
          normative: [
            { itemId: 1, name: 'Junetina sirovina', quantity: 0.8, unit: 'kg' },
            { itemId: 7, name: 'Luk suh', quantity: 0.15, unit: 'kg' },
            { itemId: 8, name: 'Češnjak', quantity: 0.05, unit: 'kg' },
            { itemId: 5, name: 'Sol', quantity: 0.02, unit: 'kg' }
          ]
        },
        {
          id: 104,
          name: 'Kobasice',
          code: 'KOB-001',
          unit: 'kg',
          normative: [
            { itemId: 3, name: 'Svinjetina sirovina', quantity: 0.9, unit: 'kg' },
            { itemId: 4, name: 'Mirodije mix', quantity: 0.08, unit: 'kg' },
            { itemId: 5, name: 'Sol', quantity: 0.02, unit: 'kg' }
          ]
        },
        {
          id: 105,
          name: 'Mesne okruglice',
          code: 'OKR-001',
          unit: 'kg',
          normative: [
            { itemId: 1, name: 'Junetina sirovina', quantity: 0.6, unit: 'kg' },
            { itemId: 3, name: 'Svinjetina sirovina', quantity: 0.3, unit: 'kg' },
            { itemId: 7, name: 'Luk suh', quantity: 0.08, unit: 'kg' },
            { itemId: 8, name: 'Češnjak', quantity: 0.03, unit: 'kg' }
          ]
        }
      ],

      rules: {
        required: v => !!v || 'Obavezno polje',
        positive: v => v > 0 || 'Količina mora biti veća od 0'
      }
    }
  },
  computed: {
    totalNormativeWeight () {
      if (!this.product || !this.product.normative) return 0
      return this.product.normative.reduce((sum, item) => sum + item.quantity, 0)
    }
  },
  watch: {
    product (newProduct) {
      this.$emit('input', newProduct)
    }
  },
  methods: {
    onProductChange () {
      if (this.product) {
        // Reset quantity to 1 when product changes
        this.quantity = 1
        this.onQuantityChange()
      }
    },
    onWarehouseChange () {
      this.$emit('warehouse-selected', this.warehouse)
    },
    onQuantityChange () {
      this.$emit('quantity-changed', this.quantity)
    },
    calculatePercentage (item) {
      if (!this.totalNormativeWeight) return 0
      return (item.quantity / this.totalNormativeWeight) * 100
    }
  }
}
</script>

<style scoped>
.v-card {
  transition: all 0.3s;
}
</style>
